/**
 * ATL-001483 — when a mailbox in auto-create mode turns an email into a load
 * (or a tender / quote) with no one clicking anything, the server broadcasts
 * `email-intake:auto-created` and every signed-in screen of the org that may
 * see the record shows a toast with its number and an "Open" action. Loads
 * created by a person (PDF import, an approved email) already toast on that
 * person's own screen.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWebSocketEvent } from '../../hooks/use-websocket';
import { useToast } from '../../core/components/ui/ToastProvider';
import { usePermissionsStore } from '../../stores/permissions-store';
import { quoteHref } from './email-intake-ui';

/** Mirrors the server's EMAIL_INTAKE_AUTO_CREATED_EVENT. */
export const EMAIL_INTAKE_AUTO_CREATED_EVENT = 'email-intake:auto-created';

export interface AutoCreatedEvent {
  outputType: 'load' | 'tender' | 'quote';
  displayId: string | null;
  loadId: string | null;
  quoteId: string | null;
  module: string | null;
  proposalId: string;
}

function isAutoCreatedEvent(v: unknown): v is AutoCreatedEvent {
  const e = v as Partial<AutoCreatedEvent> | null;
  return !!e && (e.outputType === 'load' || e.outputType === 'tender' || e.outputType === 'quote');
}

export function EmailIntakeAutoCreatedToasts(): null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const can = usePermissionsStore((s) => s.can);

  useWebSocketEvent(EMAIL_INTAKE_AUTO_CREATED_EVENT, (data) => {
    if (!isAutoCreatedEvent(data)) return;
    const isLoad = data.outputType === 'load';
    if (!(isLoad ? can('loads', 'view') : can('quotes', 'view'))) return;
    const recordId = isLoad ? data.loadId : data.quoteId;
    const id = data.displayId ?? recordId?.slice(0, 8) ?? '';
    const href = isLoad
      ? `/loads/${data.loadId}`
      : data.outputType === 'quote'
        ? quoteHref(data.quoteId ?? '', data.module)
        : '/dispatch-loads/tender';
    showToast(
      t(`email-intake.toast.autoCreated.${data.outputType}`, { id }),
      'success',
      10000,
      recordId ? { label: t('email-intake.toast.open'), onClick: () => navigate(href) } : undefined,
    );
  });

  return null;
}
